import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  createCollectionReceiptDraft,
  createCollectionReceiptWithAttachments,
  updateCollectionReceiptDraft,
} from '#/api/crmMgmt/customerOrder/collection';
import { ATTACHMENT_POLICY_STANDARD } from '#/api/platform/attachments';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import { AppDialog } from '#/modules/commonComponents/dialog';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import {
  apiToLocalDateTime,
  buildDefaultCredits,
  compactCredits,
  localDateTimeToApi,
  nowLocalDateTime,
  validateCredits,
} from '#/modules/crmMgmt/customerOrder/helper/collection';

import CollectionCreditsEditor from './CollectionCreditsEditor';
import useCollectionMutation from './useCollectionMutation';

/**
 * 實收草稿：新增（可同時附憑證、可直接確認）或修改既有草稿。
 * 有附件或直接確認時走 with-attachments（需 Idempotency-Key）。
 */
const CollectionReceiptDraftDialog = ({
  open,
  onOpenChange,
  customerOrderId,
  receipt,
  summaryPhases,
  canConfirm,
}) => {
  const isEdit = Boolean(receipt);
  const [amount, setAmount] = useState(receipt?.amount != null ? String(receipt.amount) : '');
  const [receivedAt, setReceivedAt] = useState(
    receipt?.actualReceivedAt ? apiToLocalDateTime(receipt.actualReceivedAt) : nowLocalDateTime(),
  );
  const [remark, setRemark] = useState(receipt?.remark ?? '');
  const [files, setFiles] = useState([]);
  const [attachmentRemark, setAttachmentRemark] = useState('');
  const [confirmNow, setConfirmNow] = useState(false);
  const [credits, setCredits] = useState([]);
  // 使用者沒手動改過抵付時，金額變動就重算預設抵付
  const [creditsTouched, setCreditsTouched] = useState(false);
  const [formError, setFormError] = useState(null);

  const mutation = useCollectionMutation(
    customerOrderId,
    async (variables, { idempotencyKey }) => {
      if (isEdit) {
        return updateCollectionReceiptDraft(customerOrderId, receipt.id, {
          expectedVersionNo: receipt.versionNo,
          draft: variables.draft,
        });
      }
      if (variables.files.length > 0 || variables.confirm) {
        return createCollectionReceiptWithAttachments(customerOrderId, variables, { idempotencyKey });
      }
      return createCollectionReceiptDraft(customerOrderId, variables.draft);
    },
    {
      successMessage: isEdit ? '已更新收款草稿' : '已建立收款',
      onSuccess: () => onOpenChange(false),
    },
  );

  function toggleConfirmNow(checked) {
    setConfirmNow(checked);
    if (checked && !creditsTouched) {
      setCredits(buildDefaultCredits(amount, summaryPhases));
    }
  }

  function handleAmountChange(value) {
    setAmount(value);
    if (confirmNow && !creditsTouched) {
      setCredits(buildDefaultCredits(value, summaryPhases));
    }
  }

  function handleCreditsChange(next) {
    setCreditsTouched(true);
    setCredits(next);
  }

  function handleConfirm() {
    const actualReceivedAt = localDateTimeToApi(receivedAt);
    let error = null;
    if (!(Number(amount) > 0)) {
      error = '實收金額須大於 0';
    } else if (!actualReceivedAt) {
      error = '請填寫實際收款時間';
    } else if (confirmNow) {
      error = validateCredits(amount, credits, summaryPhases);
    }
    setFormError(error);
    if (error || mutation.isPending) {
      return;
    }
    mutation.mutate({
      draft: { amount: Number(amount), actualReceivedAt, remark: remark.trim() || null },
      credits: confirmNow ? compactCredits(credits) : [],
      confirm: confirmNow,
      files,
      attachmentRemark: attachmentRemark.trim() || undefined,
    });
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? '修改收款草稿' : '登錄收款'}
      description="草稿不計入已收款；確認後金額與收款時間不可再改，錯誤只能沖銷後重登。"
      headerVariant="muted"
      maxWidthClassName="sm:max-w-xl"
      closeOnOutsideClick={false}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText={mutation.isPending ? '送出中…' : (confirmNow ? '建立並確認' : '儲存草稿')}
      confirmDisabled={mutation.isPending}
      cancelText="取消"
    >
      <div className="space-y-3 pt-2.5">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <FloatingInput
            id="collection-receipt-amount"
            type="number"
            label="實收金額"
            min={0}
            step="0.01"
            value={amount}
            onChange={(event) => handleAmountChange(event.target.value)}
          />
          <FloatingInput
            id="collection-receipt-received-at"
            type="datetime-local"
            label="實際收款時間"
            value={receivedAt}
            onChange={(event) => setReceivedAt(event.target.value)}
          />
        </div>
        <FloatingTextarea
          id="collection-receipt-remark"
          label="備註（可選）"
          value={remark}
          onChange={(event) => setRemark(event.target.value)}
          rows={2}
        />

        {isEdit ? null : (
          <>
            <div className="space-y-1">
              <label htmlFor="collection-receipt-files" className="text-sm text-slate-600">
                收款憑證（選填，{ATTACHMENT_POLICY_STANDARD.label}）
              </label>
              <input
                id="collection-receipt-files"
                type="file"
                multiple
                accept={ATTACHMENT_POLICY_STANDARD.accept}
                className="block w-full text-sm"
                onChange={(event) => setFiles([...(event.target.files ?? [])])}
              />
              {files.length > 0 ? (
                <FloatingInput
                  id="collection-receipt-attachment-remark"
                  label="憑證說明（可選）"
                  value={attachmentRemark}
                  onChange={(event) => setAttachmentRemark(event.target.value)}
                />
              ) : null}
            </div>
            {canConfirm ? (
              <label className="flex items-center gap-2 text-sm" htmlFor="collection-receipt-confirm-now">
                <Checkbox
                  id="collection-receipt-confirm-now"
                  checked={confirmNow}
                  onCheckedChange={(checked) => toggleConfirmNow(Boolean(checked))}
                />
                建立後直接確認實收
              </label>
            ) : null}
            {confirmNow ? (
              <CollectionCreditsEditor
                summaryPhases={summaryPhases}
                credits={credits}
                onChange={handleCreditsChange}
                idPrefix="collection-receipt-credit"
              />
            ) : null}
          </>
        )}
        {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
      </div>
    </AppDialog>
  );
};

CollectionReceiptDraftDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  customerOrderId: PropTypes.string.isRequired,
  receipt: PropTypes.object,
  summaryPhases: PropTypes.arrayOf(PropTypes.object).isRequired,
  canConfirm: PropTypes.bool,
};

export default CollectionReceiptDraftDialog;
